// Prebuilt formats of «Crear creativo» (report 109). One source of truth: the Studio's experience catalog
// (`EXPERIENCES`) names and describes them; this module says what a person can set WITHOUT AI and builds the
// document with the builders the Studio already uses (game starters, carousel, video, survey, elements). Pure:
// no React, no network. Every document it returns passed the one Creative Engine validator, and the gallery
// offers a format in a size only when the served runtime executes it there with no gaps (ADR-0029).
import {
  applyAndValidate,
  createSkeletonDocument,
  GAME_STARTER_LIMITS,
  gameStarter,
  type AssetRef,
  type CreativeDocument,
  type CreativeOperation,
  type Element,
  type GameStarterKind,
} from "@interactads/creative-model";
import { documentRuntimeGaps } from "@interactads/creative-runtime/support";
import { setEffect } from "../studio/core/animation";
import { pin, setMainLink } from "../studio/core/commands";
import {
  CAROUSEL_MAX_SLIDES,
  CAROUSEL_MIN_SLIDES,
  addSlide,
  carouselSlides,
  newCarousel,
  newSurvey,
  newVideo,
  setSlideImage,
  slideParts,
} from "../studio/core/components";
import { EXPERIENCES, type Experience, type ExperienceProduct } from "../studio/core/experiences";
import { replaceWithImage } from "../studio/core/game";
import { layoutSize } from "../studio/core/model";

/** A copy of `o` without the key `k`. */
function without<T extends object, K extends keyof T>(o: T, k: K): Omit<T, K> {
  return Object.fromEntries(Object.entries(o).filter(([key]) => key !== k)) as Omit<T, K>;
}

export type PrebuiltValues = Readonly<Record<string, string | number | undefined>>;
/** A picked library file: the canonical reference to its current revision (`pinnedRef` of the Studio). */
export type PickedAsset = Omit<AssetRef, "key"> & { readonly name: string };
export interface PrebuiltMedia {
  readonly image?: PickedAsset;
  readonly logo?: PickedAsset;
  readonly video?: PickedAsset;
  /** Memory: one picture per pair; carousel: one per slide. */
  readonly pictures?: readonly PickedAsset[];
}

export type FieldKind = "text" | "color" | "url" | "number";
export interface PrebuiltField {
  readonly key: string;
  readonly label: string;
  readonly kind: FieldKind;
  readonly hint?: string;
  readonly min?: number;
  readonly max?: number;
  readonly maxLength?: number;
}
export type MediaSlot = "image" | "logo" | "video" | "pictures";

export interface Prebuilt {
  readonly id: string;
  readonly experience: Experience;
  readonly category: ExperienceProduct;
  readonly fields: readonly PrebuiltField[];
  readonly media: readonly MediaSlot[];
  /** A file without which the format cannot be built (the video of a video banner). */
  readonly requires?: MediaSlot;
  readonly defaults: PrebuiltValues;
  build(base: CreativeDocument, values: PrebuiltValues, media: PrebuiltMedia): CreativeDocument;
}

export const CATEGORY_ORDER: readonly ExperienceProduct[] = [
  "RICH_MEDIA_STANDARD",
  "ADVERGAMING",
  "VIDEO_RICH_MEDIA",
  "SURVEY_RICH_MEDIA",
];

// ─── Building blocks ──────────────────────────────────────────────────────────

type Ops = CreativeOperation[];
const HEX = /^#[0-9a-f]{6}$/i;
const color = (v: unknown, fallback: string) => (typeof v === "string" && HEX.test(v) ? v : fallback);
const str = (v: unknown, fallback: string, max = 120) => {
  const s = typeof v === "string" ? v.trim().slice(0, max) : "";
  return s === "" ? fallback : s;
};
const num = (v: unknown, fallback: number, min: number, max: number) => {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : Number.NaN;
  return Number.isFinite(n) ? Math.max(min, Math.min(max, Math.round(n))) : fallback;
};
const url = (v: unknown) => {
  if (typeof v !== "string" || v.trim() === "") return undefined;
  try {
    const u = new URL(v.trim());
    return u.protocol === "https:" || u.protocol === "http:" ? u.toString() : undefined;
  } catch {
    return undefined;
  }
};

/** Applies operations and keeps the result only if the one validator accepts it. */
class Doc {
  constructor(public doc: CreativeDocument) {}
  apply(ops: Ops): this {
    if (ops.length === 0) return this;
    const r = applyAndValidate(this.doc, ops);
    if (!r.document) throw new Error(`PREBUILT_INVALID: ${JSON.stringify(r.issues.slice(0, 3))}`);
    this.doc = r.document;
    return this;
  }
  get layout() {
    const l = this.doc.layouts[0];
    if (!l) throw new Error("PREBUILT_NO_LAYOUT");
    return l;
  }
  get size() {
    return layoutSize(this.layout);
  }
  scene() {
    return this.doc.initialSceneId;
  }
  free(prefix: string) {
    const taken = new Set([...this.doc.elements.map((e) => e.id), ...this.doc.scenes.map((s) => s.id)]);
    let id = prefix;
    for (let n = 2; taken.has(id); n++) id = `${prefix}${String(n)}`;
    return id;
  }
  /** Pins a picked library file and returns its key. */
  pin(asset: PickedAsset, role: NonNullable<AssetRef["role"]>, prefix: string): string {
    const ref = without(asset, "name"); // the library name is not part of the document reference
    const p = pin(this.doc, { ...ref, role }, prefix);
    this.apply(p.ops);
    return p.key;
  }
  add(element: Element, sceneId = this.scene()): string {
    this.apply([{ op: "addElement", element, parent: { in: "scene", id: sceneId } }]);
    return element.id;
  }
  enter(id: string, at: number, kind: "fade" | "slide" = "fade") {
    this.apply(
      setEffect(this.doc, this.layout.id, id, "entrance", { kind, at, duration: 500, side: "bottom" } as never)
    );
  }
  background(c: string, sceneId = this.scene()) {
    const scene = this.doc.scenes.find((s) => s.id === sceneId);
    if (!scene) return;
    this.apply([{ op: "updateScene", id: sceneId, set: { background: { kind: "solid", color: c } } } as never]);
  }
}

const frame = (x: number, y: number, w: number, h: number) => ({
  x: Math.round(x),
  y: Math.round(y),
  width: Math.max(8, Math.round(w)),
  height: Math.max(8, Math.round(h)),
});
const font = (w: number, h: number, div: number, min: number, max: number) =>
  Math.max(min, Math.min(max, Math.round(Math.min(w, h * 1.4) / div)));
const wide = (w: number, h: number) => w / h > 1.6;

function text(
  id: string,
  name: string,
  value: string,
  f: ReturnType<typeof frame>,
  size: number,
  c: string,
  weight = 700
): Element {
  return {
    id,
    type: "text",
    name,
    frame: f,
    content: { runs: [{ text: value }] },
    typography: { size, weight, color: c, align: "center", verticalAlign: "middle", lineHeight: 1.15 },
  };
}
function button(id: string, label: string, f: ReturnType<typeof frame>, fill: string, size: number): Element {
  return {
    id,
    type: "button",
    name: "Botón",
    role: "cta",
    frame: f,
    label: { runs: [{ text: label }] },
    typography: { size, weight: 700, color: "#FFFFFF", align: "center", verticalAlign: "middle" },
    style: { fill: { kind: "solid", color: fill }, radius: 6 },
    interactive: { cursor: "pointer" },
  };
}
function image(
  id: string,
  name: string,
  key: string,
  alt: string,
  f: ReturnType<typeof frame>,
  fit: "contain" | "cover" = "contain"
): Element {
  return { id, type: "image", name, asset: key, fit, alt: alt.slice(0, 300), frame: f };
}

/** A CTA that opens the main link: the link is set first, then a tap rule on the button. */
function linkButton(d: Doc, id: string, link: string | undefined) {
  if (!link) return;
  d.apply(setMainLink(d.doc, link));
  const main = d.doc.links[0]?.id;
  if (main)
    d.apply([
      {
        op: "addRule",
        rule: {
          id: d.free(`${id}Tap`),
          on: { type: "pointer.tap", target: id },
          do: [{ type: "link.open", link: main }],
        },
      } as never,
    ]);
}

/** Logo in the top-left corner of a scene, at its own proportions. */
function logo(d: Doc, asset: PickedAsset | undefined, sceneId = d.scene()) {
  if (!asset) return;
  const { width: W, height: H } = d.size;
  const box = Math.min(W, H) * 0.18;
  const ratio = (asset.width ?? 1) / (asset.height ?? 1);
  const w = ratio >= 1 ? box : box * ratio;
  const h = ratio >= 1 ? box / ratio : box;
  const key = d.pin(asset, "content", "logo");
  d.add(image(d.free("logo"), "Logotipo", key, asset.name, frame(W * 0.04, H * 0.04, w, h)), sceneId);
}

// ─── Common fields ──────────────────────────────────────────────────────────────

const F = {
  headline: { key: "headline", label: "Titular", kind: "text", maxLength: 80 },
  subline: { key: "subline", label: "Texto de apoyo", kind: "text", maxLength: 120 },
  cta: { key: "cta", label: "Texto del botón", kind: "text", maxLength: 30 },
  link: { key: "link", label: "Enlace de destino", kind: "url", hint: "https://… (opcional)" },
  accent: { key: "accent", label: "Color principal", kind: "color" },
  background: { key: "background", label: "Fondo", kind: "color" },
  textColor: { key: "textColor", label: "Color del texto", kind: "color" },
} as const satisfies Record<string, PrebuiltField>;

// ─── Rich Media, Video, Survey ──────────────────────────────────────────────────

function banner(base: CreativeDocument, v: PrebuiltValues, m: PrebuiltMedia): CreativeDocument {
  const d = new Doc(base);
  const { width: W, height: H } = d.size;
  const accent = color(v.accent, "#E4002B");
  const ink = color(v.textColor, "#111827");
  d.background(color(v.background, "#FFFFFF"));
  logo(d, m.logo);
  const side = wide(W, H);
  let imageId: string | undefined;
  if (m.image) {
    const key = d.pin(m.image, "content", "img");
    const f = side ? frame(W * 0.04, H * 0.1, W * 0.36, H * 0.8) : frame(W * 0.1, H * 0.2, W * 0.8, H * 0.36);
    imageId = d.add(image(d.free("product"), "Producto", key, m.image.name, f));
  }
  const tx = side ? W * 0.44 : W * 0.06;
  const tw = side ? W * 0.52 : W * 0.88;
  const top = side ? H * 0.12 : m.image ? H * 0.58 : H * 0.22;
  const head = d.add(
    text(
      d.free("headline"),
      "Titular",
      str(v.headline, "Tu titular aquí", 80),
      frame(tx, top, tw, H * (side ? 0.3 : 0.14)),
      font(W, H, 11, 14, 40),
      ink,
      800
    )
  );
  const sub = d.add(
    text(
      d.free("subline"),
      "Texto de apoyo",
      str(v.subline, "Un beneficio claro en una frase.", 120),
      frame(tx, top + H * (side ? 0.32 : 0.15), tw, H * (side ? 0.2 : 0.1)),
      font(W, H, 22, 10, 20),
      ink,
      500
    )
  );
  const bw = Math.min(tw, Math.max(96, W * (side ? 0.28 : 0.5)));
  const bh = Math.max(28, Math.min(48, H * (side ? 0.22 : 0.08)));
  const cta = d.add(
    button(
      d.free("cta"),
      str(v.cta, "Comprar", 30),
      frame(tx + (tw - bw) / 2, side ? H * 0.68 : H * 0.86 - bh / 2, bw, bh),
      accent,
      font(W, H, 18, 11, 18)
    )
  );
  linkButton(d, cta, url(v.link));
  [imageId, head, sub, cta].forEach((id, i) => {
    if (id) d.enter(id, i * 200, i === 0 ? "fade" : "slide");
  });
  return d.doc;
}

function carousel(base: CreativeDocument, v: PrebuiltValues, m: PrebuiltMedia): CreativeDocument {
  const d = new Doc(base);
  const { width: W, height: H } = d.size;
  d.background(color(v.background, "#FFFFFF"));
  const built = newCarousel(d.doc, d.layout.id, d.scene(), d.size);
  d.apply(built.ops);
  const want = num(v.slides, 3, CAROUSEL_MIN_SLIDES, Math.min(6, CAROUSEL_MAX_SLIDES));
  while (carouselSlides(d.doc, built.id).length < want) {
    const next = addSlide(d.doc, built.id);
    if (!next) break;
    d.apply(next.ops);
  }
  const slides = carouselSlides(d.doc, built.id);
  slides.forEach((slideId, i) => {
    const caption = slideParts(d.doc, slideId)?.caption;
    const t = str(v[`slide${String(i + 1)}`], "", 60);
    if (caption && t)
      d.apply([{ op: "updateElement", id: caption.id, set: { content: { runs: [{ text: t }] } } } as never]);
    const pic = m.pictures?.[i];
    if (pic) d.apply(setSlideImage(d.doc, built.id, slideId, d.pin(pic, "content", "slide"), pic.name));
  });
  d.apply([
    {
      op: "updateElement",
      id: built.id,
      set: {
        props: {
          ...(d.doc.elements.find((e) => e.id === built.id) as { props: object }).props,
          accentColor: color(v.accent, "#111827"),
        },
      },
    } as never,
  ]);
  logo(d, m.logo);
  const bw = Math.max(96, W * 0.5);
  const bh = Math.max(28, Math.min(44, H * 0.08));
  const cta = d.add(
    button(
      d.free("cta"),
      str(v.cta, "Ver más", 30),
      frame((W - bw) / 2, H * 0.92 - bh, bw, bh),
      color(v.accent, "#111827"),
      font(W, H, 18, 11, 18)
    )
  );
  linkButton(d, cta, url(v.link));
  return d.doc;
}

function videoBanner(base: CreativeDocument, v: PrebuiltValues, m: PrebuiltMedia): CreativeDocument {
  if (!m.video) throw new Error("PREBUILT_NEEDS_VIDEO");
  const d = new Doc(base);
  const { width: W, height: H } = d.size;
  const ink = color(v.textColor, "#FFFFFF");
  d.background(color(v.background, "#111827"));
  // The CTA lives in its own band (below, or beside in wide sizes): never over the video's controls bar.
  const side = wide(W, H);
  const bw = side ? Math.max(96, W * 0.2) : Math.max(96, W * 0.42);
  const bh = side ? Math.max(28, Math.min(44, H * 0.5)) : Math.max(28, Math.min(44, H * 0.1));
  const gap = Math.max(8, (side ? W : H) * 0.03);
  const area = side ? { width: W - bw - gap * 2, height: H } : { width: W, height: H - bh - gap * 2 };
  const key = d.pin(m.video, "content", "vid");
  const vid = newVideo(
    d.free("video"),
    area,
    { key, asset: { ...m.video, key, role: "content" }, name: m.video.name },
    [d.layout.id]
  );
  d.add(without(vid, "onlyIn") as Element);
  logo(d, m.logo);
  d.add(
    text(
      d.free("headline"),
      "Titular",
      str(v.headline, "Tu titular aquí", 80),
      frame(area.width * 0.06, H * 0.06, area.width * 0.88, H * 0.16),
      font(W, H, 12, 12, 34),
      ink,
      800
    )
  );
  const cta = d.add(
    button(
      d.free("cta"),
      str(v.cta, "Ver más", 30),
      side ? frame(W - gap - bw, (H - bh) / 2, bw, bh) : frame((W - bw) / 2, H - gap - bh, bw, bh),
      color(v.accent, "#E4002B"),
      font(W, H, 18, 11, 18)
    )
  );
  linkButton(d, cta, url(v.link));
  return d.doc;
}

function survey(base: CreativeDocument, v: PrebuiltValues, m: PrebuiltMedia): CreativeDocument {
  const d = new Doc(base);
  d.background(color(v.background, "#FFFFFF"));
  const built = newSurvey(d.doc, str(v.objective, "Conocer tu opinión", 120));
  if (!built) throw new Error("PREBUILT_SURVEY");
  d.apply(built.ops);
  d.apply([{ op: "setInitialScene", id: built.sceneId } as never]);
  logo(d, m.logo, built.sceneId);
  return d.doc;
}

// ─── Advergames ──────────────────────────────────────────────────────────────────

const GAME_FIELDS = (kind: GameStarterKind): PrebuiltField[] => [
  { key: "title", label: "Título", kind: "text", maxLength: 80 },
  { key: "howTo", label: "Cómo se juega", kind: "text", maxLength: 120 },
  { key: "play", label: "Botón para empezar", kind: "text", maxLength: 30 },
  ...(kind === "quiz"
    ? []
    : [
        {
          key: "seconds",
          label: "Tiempo de juego (segundos)",
          kind: "number" as const,
          min: GAME_STARTER_LIMITS.durationMs.min / 1000,
          max: GAME_STARTER_LIMITS.durationMs.max / 1000,
        },
      ]),
  ...(kind === "pairs"
    ? [
        {
          key: "pairs",
          label: "Cantidad de parejas",
          kind: "number" as const,
          min: GAME_STARTER_LIMITS.pairs.min,
          max: GAME_STARTER_LIMITS.pairs.max,
        },
      ]
    : []),
  { key: "winTitle", label: "Mensaje al ganar", kind: "text", maxLength: 60 },
  { key: "loseTitle", label: "Mensaje al perder", kind: "text", maxLength: 60 },
  F.cta,
  F.link,
  F.accent,
  F.background,
];

function game(kind: GameStarterKind) {
  return (base: CreativeDocument, v: PrebuiltValues, m: PrebuiltMedia): CreativeDocument => {
    const d = new Doc(base);
    const link = url(v.link);
    // The starter adds its «Ver más» button only when the document has a main link.
    if (link) d.apply(setMainLink(d.doc, link));
    const seconds = v.seconds === undefined || v.seconds === "" ? undefined : num(v.seconds, 60, 5, 600);
    const starter = gameStarter(d.doc, kind, {
      accent: color(v.accent, "#E4002B"),
      makeInitial: true,
      texts: {
        title: str(v.title, ""),
        howTo: str(v.howTo, ""),
        play: str(v.play, ""),
        winTitle: str(v.winTitle, ""),
        loseTitle: str(v.loseTitle, ""),
        cta: str(v.cta, ""),
      },
      ...(seconds !== undefined ? { durationMs: seconds * 1000 } : {}),
      ...(kind === "pairs" && v.pairs !== undefined
        ? { pairs: num(v.pairs, 4, GAME_STARTER_LIMITS.pairs.min, GAME_STARTER_LIMITS.pairs.max) }
        : {}),
      ...(typeof v.background === "string" && HEX.test(v.background) ? { background: v.background } : {}),
    });
    d.apply(starter.ops);
    // The empty scene the skeleton started with is no longer reachable: drop it.
    const start = base.initialSceneId;
    if (
      start !== starter.introSceneId &&
      d.doc.scenes.some((s) => s.id === start) &&
      d.doc.scenes.find((s) => s.id === start)?.children.length === 0
    )
      d.apply([{ op: "removeScene", id: start } as never]);
    logo(d, m.logo, starter.introSceneId);
    // Memory: one picture per pair, on both of its cards (the card keeps its pair and its matched state).
    if (kind === "pairs" && m.pictures?.length) {
      const pairs = d.doc.game?.pairs?.cards ?? [];
      pairs.forEach((pair, i) => {
        const pic = m.pictures?.[i];
        if (!pic) return;
        const key = d.pin(pic, "content", "card");
        for (const card of pair)
          d.apply(replaceWithImage(d.doc, card, { key, asset: { ...pic, key, role: "content" }, alt: pic.name }));
      });
    }
    return d.doc;
  };
}

// ─── Catalog ─────────────────────────────────────────────────────────────────────

const NON_GAMES: Record<string, Omit<Prebuilt, "id" | "experience" | "category">> = {
  banner: {
    fields: [F.headline, F.subline, F.cta, F.link, F.accent, F.background, F.textColor],
    media: ["image", "logo"],
    defaults: {
      headline: "",
      subline: "",
      cta: "Comprar",
      accent: "#E4002B",
      background: "#FFFFFF",
      textColor: "#111827",
    },
    build: banner,
  },
  carousel: {
    fields: [
      { key: "slides", label: "Cantidad de diapositivas", kind: "number", min: CAROUSEL_MIN_SLIDES, max: 6 },
      { key: "slide1", label: "Texto de la diapositiva 1", kind: "text", maxLength: 60 },
      { key: "slide2", label: "Texto de la diapositiva 2", kind: "text", maxLength: 60 },
      { key: "slide3", label: "Texto de la diapositiva 3", kind: "text", maxLength: 60 },
      F.cta,
      F.link,
      F.accent,
      F.background,
    ],
    media: ["pictures", "logo"],
    defaults: { slides: 3, cta: "Ver más", accent: "#111827", background: "#FFFFFF" },
    build: carousel,
  },
  "video-banner": {
    fields: [F.headline, F.cta, F.link, F.accent, F.background, F.textColor],
    media: ["video", "logo"],
    requires: "video",
    defaults: { cta: "Ver más", accent: "#E4002B", background: "#111827", textColor: "#FFFFFF" },
    build: videoBanner,
  },
  survey: {
    fields: [
      {
        key: "objective",
        label: "¿Qué quieres saber?",
        kind: "text",
        maxLength: 120,
        hint: "Las preguntas y sus caminos se editan en el Studio.",
      },
      F.background,
    ],
    media: ["logo"],
    defaults: { background: "#FFFFFF" },
    build: survey,
  },
};

export const PREBUILT: readonly Prebuilt[] = EXPERIENCES.map((x): Prebuilt => {
  const common = { id: x.id, experience: x, category: x.product };
  if (x.game) {
    const kind = x.game;
    return {
      ...common,
      fields: GAME_FIELDS(kind),
      media: kind === "pairs" ? ["logo", "pictures"] : ["logo"],
      defaults: { accent: "#E4002B", background: "#FFFFFF", cta: "Ver más", ...(kind === "pairs" ? { pairs: 4 } : {}) },
      build: game(kind),
    };
  }
  const rest = NON_GAMES[x.id];
  if (!rest) throw new Error(`prebuilt without builder: ${x.id}`);
  return { ...common, ...rest };
});

export const prebuiltById = (id: string) => PREBUILT.find((p) => p.id === id) ?? null;

/** The empty document the Studio would open for this creative (the same skeleton, CREATIVE-MODEL §14.1). */
export function baseDocument(product: ExperienceProduct, format: string, name: string): CreativeDocument | null {
  const m = /^(\d{1,4})x(\d{1,4})$/.exec(format);
  if (!m) return null;
  const r = createSkeletonDocument({
    intendedProduct: product,
    locale: "es",
    accessibilityLabel: (name.trim() || "Anuncio").slice(0, 200),
    format: { key: format, width: Number(m[1]), height: Number(m[2]) },
  });
  return r.ok ? r.document : null;
}

/** Builds the format and checks the served runtime executes every format of it with no gaps. */
export function buildPrebuilt(
  p: Prebuilt,
  base: CreativeDocument,
  values: PrebuiltValues,
  media: PrebuiltMedia
): CreativeDocument {
  const doc = p.build(base, { ...p.defaults, ...values }, media);
  const gaps = doc.layouts.flatMap((l) => documentRuntimeGaps(doc, l.id));
  if (gaps.length > 0) throw new Error(`PREBUILT_RUNTIME_GAPS: ${gaps.map((g) => g.kind).join(",")}`);
  return doc;
}

const sizeCache = new Map<string, boolean>();
/** True when the format builds and runs in this size (with a stand-in video for the video banner). */
export function worksIn(p: Prebuilt, format: string): boolean {
  const k = `${p.id}|${format}`;
  const cached = sizeCache.get(k);
  if (cached !== undefined) return cached;
  let ok = false;
  try {
    const base = baseDocument(p.category, format, p.experience.name);
    if (base) {
      buildPrebuilt(p, base, {}, p.requires === "video" ? { video: STAND_IN_VIDEO } : {});
      ok = true;
    }
  } catch {
    ok = false;
  }
  sizeCache.set(k, ok);
  return ok;
}

/** Only to check sizes: never pinned into a saved document. */
const STAND_IN_VIDEO: PickedAsset = {
  assetId: "01900000-0000-7000-8000-000000000001",
  revisionId: "01900000-0000-7000-8000-000000000002",
  sha256: "0".repeat(64),
  kind: "video",
  mime: "video/mp4",
  bytes: 1,
  width: 1280,
  height: 720,
  durationMs: 10_000,
  role: "content",
  name: "Video",
};
